import { CreditCardIcon, GlobeAltIcon, MoonIcon, PhoneIcon, ScaleIcon, ShieldCheckIcon, SunIcon } from '@heroicons/react/20/solid';
import { createContext, type PropsWithChildren, useContext, useState } from 'react';
import { useLocale } from 'remix-i18next/react';
import { useTypedRouteLoaderData } from 'remix-typedjson';
import { ColoredCrispyVoiceLogoWithText } from '#common/components/ColoredCrispyVoiceLogoWithText.tsx';
import LanguagePicker from '#common/components/LanguagePicker.tsx';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { useTranslatedPath } from '#common/utils/i18n.tsx';
import { LinkTypeIcon, type LinkType } from '#common/utils/link-types.tsx';
import { useCoreTheme, type CoreTheme } from '#speaker-shop/hooks/useCoreTheme.ts';
import { type loader as rootLoader } from '#speaker-shop/root';
import { getWebsiteUrl } from '#speaker-shop/utils/public-env.tsx';
import { ContactFormDialog } from './ContactFormDialog.tsx';

const trustIcons = [ ShieldCheckIcon, ScaleIcon, CreditCardIcon, GlobeAltIcon ];
const CoreLegalThemeContext = createContext<CoreTheme | null>(null);

export function useCoreLegalTheme() {
    return useContext(CoreLegalThemeContext);
}

export function CoreLegalPageLayout({ links, children }: PropsWithChildren<{
    links: Array<{ type: string; url: string }>;
}>) {
    const { baseUrl } = useTypedRouteLoaderData<typeof rootLoader>('root')!;
    const { theme, toggleTheme } = useCoreTheme();
    const { t, rawT } = useEditableTranslation('core-shop');
    const { t: tFooter } = useEditableTranslation('footer');
    const translatePath = useTranslatedPath(useLocale());
    const [ showContactForm, setShowContactForm ] = useState(false);

    return (
        <CoreLegalThemeContext.Provider value={theme}>
            <div className='core-unified core-legal-shell' data-theme={theme}>
                <nav className='core-nav'>
                    <a className='core-logo' href={translatePath('/')} aria-label='CrispyVoice'>
                        <ColoredCrispyVoiceLogoWithText className='core-logo-mark' />
                    </a>
                    <div className='core-nav-trust'>
                        {([ 'voice-owner-approved', 'ai-act-ready', 'card-payment', 'languages' ] as const).map(
                            (key, index) => {
                                const Icon = trustIcons[index]!;
                                return (
                                    <span className='core-trust-pill' key={key}>
                                        <Icon className='size-3' /> {t(`topbar.${key}`)}
                                    </span>
                                );
                            }
                        )}
                    </div>
                    <div className='core-nav-actions'>
                        <button
                            type='button'
                            className='core-icon-button'
                            onClick={toggleTheme}
                            aria-label={rawT('nav.theme')}
                        >
                            <SunIcon className='core-theme-icon-light size-4' />
                            <MoonIcon className='core-theme-icon-dark size-4' />
                        </button>
                        <LanguagePicker baseUrl={baseUrl} className='core-language-picker' />
                        <button type='button' className='core-nav-button' onClick={() => setShowContactForm(true)}>
                            <PhoneIcon className='core-nav-button-icon size-4' />
                            <span>{t('nav.contact')}</span>
                        </button>
                    </div>
                </nav>

                <main className='core-legal-main'>{children}</main>

                <footer className='core-footer'>
                    <span className='core-footer-left'>{t('footer.left')}</span>
                    <div className='core-footer-links flex flex-wrap items-center justify-center gap-x-4 gap-y-2'>
                        {links.length > 0 && (
                            <div className='core-footer-social flex items-center gap-3'>
                                {links.map(link => (
                                    <a
                                        className='transition hover:scale-110 active:scale-95'
                                        key={`${link.type}:${link.url}`}
                                        href={link.url}
                                        target='_blank'
                                        rel='noreferrer'
                                        aria-label={link.type}
                                    >
                                        <LinkTypeIcon type={link.type as LinkType} className='size-4' />
                                    </a>
                                ))}
                            </div>
                        )}
                        <div className='core-footer-legal flex items-center gap-4'>
                            <a className='core-footer-link' href={translatePath('/gtc')}>{tFooter('gtc')}</a>
                            <a className='core-footer-link' href={translatePath('/imprint')}>{tFooter('imprint')}</a>
                        </div>
                    </div>
                    <span className='core-footer-right text-[10px] font-semibold tracking-[0.5px] text-[#ffd700]/45'>
                        Powered by <a href={getWebsiteUrl()} target='_blank' rel='noreferrer'>CrispyVoice</a>
                    </span>
                </footer>

                <ContactFormDialog
                    topic='Contact real voice'
                    description={<p>{t('common:contact-form-description')}</p>}
                    open={showContactForm}
                    onClose={() => setShowContactForm(false)}
                    appearance='unified'
                    theme={theme}
                />
            </div>
        </CoreLegalThemeContext.Provider>
    );
}
